<template>
  <ADrawer
    :width="800"
    :open="open"
    class="company-editing-drawer manager-filter-drawer manager-filter-drawer--reports"
    @close="emit('update:open', false)"
  >
    <template #title>
      <span class="company-editing-drawer__title">Фильтр</span>
    </template>

    <template #closeIcon>
      <span class="manager-filter-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <AForm layout="vertical" class="manager-filter-drawer__form">
      <div class="manager-filter-drawer__fields-row">
        <AFormItem label="Статус" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.statusId"
            allow-clear
            placeholder="Все статусы"
            :options="statusOptions"
          />
        </AFormItem>

        <AFormItem label="Отчетный период" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.year"
            allow-clear
            placeholder="Все периоды"
            :options="yearOptions"
          />
        </AFormItem>
      </div>

      <div class="manager-filter-drawer__fields-section">
        <AFormItem label="Кластер" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.clusterId"
            allow-clear
            placeholder="Все кластеры"
            :options="clusterOptions"
          />
        </AFormItem>
      </div>
    </AForm>

    <template #footer>
      <ARow align="middle" justify="end" class="manager-filter-drawer__toolbar">
        <ACol>
          <ASpace>
            <button
              type="button"
              class="btn btn-outline btn-medium"
              :disabled="isResetDisabled"
              @click="handleReset"
            >
              Сбросить
            </button>
            <button
              type="button"
              class="btn btn-primary btn-medium"
              :disabled="isApplyDisabled"
              @click="handleApply"
            >
              Применить
            </button>
          </ASpace>
        </ACol>
      </ARow>
    </template>
  </ADrawer>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import {
  Col as ACol,
  Drawer as ADrawer,
  Form as AForm,
  FormItem as AFormItem,
  Row as ARow,
  Select as ASelect,
  Space as ASpace,
} from 'ant-design-vue';
import type { ManagerReportsClusterOption, ManagerReportsFilter } from './types';

const props = defineProps<{
  open: boolean;
  clusters: ManagerReportsClusterOption[];
  statusOptions: { value: string; label: string }[];
  yearOptions: { value: string; label: string }[];
  filter: ManagerReportsFilter;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'apply', value: ManagerReportsFilter): void;
}>();

const draft = reactive<ManagerReportsFilter>({
  clusterId: undefined,
  statusId: undefined,
  year: undefined,
});

const clusterOptions = computed(() =>
  props.clusters.map((cluster) => ({
    value: cluster.id,
    label: cluster.name,
  })),
);

function normalizeFilter(filter: ManagerReportsFilter): ManagerReportsFilter {
  return {
    clusterId: filter.clusterId || undefined,
    statusId: filter.statusId || undefined,
    year: filter.year || undefined,
  };
}

function filtersEqual(a: ManagerReportsFilter, b: ManagerReportsFilter): boolean {
  const left = normalizeFilter(a);
  const right = normalizeFilter(b);

  return (
    left.clusterId === right.clusterId &&
    left.statusId === right.statusId &&
    left.year === right.year
  );
}

const hasDraftValues = computed(() => Boolean(draft.clusterId || draft.statusId || draft.year));

const hasChanges = computed(() => !filtersEqual(draft, props.filter));

const isResetDisabled = computed(() => !hasDraftValues.value);
const isApplyDisabled = computed(() => !hasChanges.value);

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;

    draft.clusterId = props.filter.clusterId;
    draft.statusId = props.filter.statusId;
    draft.year = props.filter.year;
  },
);

function handleReset() {
  draft.clusterId = undefined;
  draft.statusId = undefined;
  draft.year = undefined;
}

function handleApply() {
  emit('apply', {
    clusterId: draft.clusterId || undefined,
    statusId: draft.statusId || undefined,
    year: draft.year || undefined,
  });
  emit('update:open', false);
}
</script>
